/* FORGE chalk burst — the add-to-cart chalk-dust microinteraction.
   Fires on the PDP ATC button, the sticky-bar ATC and the drawer upsell Add.

   2026-08-14 REBUILD (Brandon: "too coarse/particulate — it reads like confetti, not chalk").
   What actually makes it read as chalk rather than confetti, in order of how much each one
   mattered when tuned in isolation:

   1. COUNT x DENSITY, NOT SIZE. 150-250 particles at 0.4-1.6px radius instead of 44 at
      1.0-3.6px. A cloud is legible because it has many elements below the eye's ability to
      resolve them individually; the moment a single dot is trackable it reads as an object.
   2. IT HANGS. Real chalk dust is near-neutrally buoyant: it drifts UP off the hand and
      lingers. Gravity is 0.012px/frame^2 here (was 0.25 — 20x too heavy, which is what made
      the old one arc and fall like thrown confetti), plus a small upward buoyancy on the
      finest particles and heavy air drag, so the cloud slows almost to a stop and floats.
   3. LATERAL TURBULENCE. Each particle carries its own phase/frequency and is pushed
      sideways by a sine field, so the cloud breathes and shears instead of expanding as a
      clean radial starburst. This is the single biggest "is it a puff or a firework" tell.
   4. SOFT EDGES. Every particle is one pre-rendered radial-gradient sprite, drawn scaled.
      A hard-edged arc() at 1px reads as a speck whatever its opacity. The sprite is built
      ONCE into an offscreen canvas and blitted with drawImage, which is far cheaper than
      ctx.filter='blur()' per frame and needs no filter support (Safari lags there).
   5. LOW PER-PARTICLE ALPHA (0.10-0.30) so density does the work, plus a size decay to ~65%
      over the life, so the cloud thins as it disperses rather than vanishing at full size.

   Life is ~2.3s with an ease-out alpha envelope and a 60ms attack (so it appears as a puff,
   not a pop). Deliberately NOT DPR-scaled: the canvas is 1 CSS px per device px, which
   softens it further for free and halves the fill cost on retina.

   ONE POOL, ONE LOOP. The old build started a fresh rAF per burst and each one cleared the
   whole canvas, so two adds within the animation window erased each other. Particles now go
   into a single array with a single loop that stops itself when the array empties.

   Guards: skipped entirely under prefers-reduced-motion; falls back to the control's own
   centre when the event has no pointer coordinates (keyboard activation); canvas is
   aria-hidden and pointer-transparent; delegation survives drawer re-renders. */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.documentElement.classList.contains('forge-motion-off')) {
    return;
  }

  var LIFE = 2300; /* ms — the settle */
  var GRAV = 0.012; /* px/frame^2 — chalk hangs; confetti does not */
  var DRAG = 0.978;
  var SPRITE = 48;

  var cv = null;
  var ctx = null;
  var sprite = null;
  var ps = [];
  var running = false;
  var last = 0;

  function makeSprite() {
    var s = document.createElement('canvas');
    s.width = s.height = SPRITE;
    var c = s.getContext('2d');
    var h = SPRITE / 2;
    var g = c.createRadialGradient(h, h, 0, h, h, h);
    /* bone, not pure white — matches the palette and stops it reading as a spark */
    g.addColorStop(0, 'rgba(238,233,225,1)');
    g.addColorStop(0.28, 'rgba(238,233,225,0.55)');
    g.addColorStop(0.62, 'rgba(238,233,225,0.16)');
    g.addColorStop(1, 'rgba(238,233,225,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, SPRITE, SPRITE);
    return s;
  }

  function fit() {
    cv.width = window.innerWidth;
    cv.height = window.innerHeight;
  }

  function ensureCanvas() {
    if (cv) {
      return;
    }
    cv = document.createElement('canvas');
    cv.setAttribute('aria-hidden', 'true');
    cv.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:90;';
    document.body.appendChild(cv);
    ctx = cv.getContext('2d');
    sprite = makeSprite();
    fit();
    window.addEventListener('resize', fit);
  }

  function burst(x, y) {
    ensureCanvas();
    var n = 150 + Math.floor(Math.random() * 100); /* 150-250 */
    for (var i = 0; i < n; i++) {
      var ang = Math.random() * Math.PI * 2;
      /* sqrt keeps the initial speeds clustered low with a thin fast tail, which is what a
         puff looks like; a flat random gives an evenly-spaced ring = firework */
      var spd = Math.sqrt(Math.random()) * 2.4;
      ps.push({
        x: x + (Math.random() - 0.5) * 10,
        y: y + (Math.random() - 0.5) * 10,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd * 0.62 - (0.35 + Math.random() * 0.75), /* biased upward */
        r: 0.4 + Math.random() * 1.2, /* 0.4-1.6px */
        a: 0.1 + Math.random() * 0.2, /* 0.10-0.30 */
        ph: Math.random() * 6.283, /* turbulence phase */
        fq: 0.9 + Math.random() * 1.8, /* turbulence frequency */
        amp: 0.03 + Math.random() * 0.07, /* turbulence strength */
        buoy: 0.006 * (1.6 - Math.random()), /* the finest dust rises longest */
        born: 0,
      });
    }
    if (!running) {
      running = true;
      last = 0;
      window.requestAnimationFrame(step);
    }
  }

  function step(ts) {
    if (!last) {
      last = ts;
    }
    /* frame-rate independent: 1.0 == a 60fps frame, clamped so a background tab that
       resumes after seconds does not teleport the cloud across the screen */
    var dt = Math.min((ts - last) / 16.667, 3);
    last = ts;

    ctx.clearRect(0, 0, cv.width, cv.height);

    /* in-place compaction: survivors are written back to the head of the array, so a long
       session of adds can never grow it without bound */
    var alive = 0;
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i];
      p.born += dt * 16.667;
      var el = p.born;
      if (el >= LIFE) {
        continue;
      }
      ps[alive++] = p;

      var t = el / LIFE;
      p.vx += Math.sin(el / 1000 * p.fq * 6.283 + p.ph) * p.amp * dt;
      p.vy += (GRAV - p.buoy) * dt;
      p.vx *= Math.pow(DRAG, dt);
      p.vy *= Math.pow(DRAG, dt);
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      /* 60ms attack, then ease-out to nothing; size decays to 65% */
      var att = el < 60 ? el / 60 : 1;
      var env = 1 - t;
      var alpha = p.a * att * env * env * Math.sqrt(env);
      if (alpha <= 0.002) {
        continue;
      }
      var s = p.r * (1 - 0.35 * t) * 5; /* sprite is mostly falloff — 5x gives an ~r core */
      ctx.globalAlpha = alpha;
      ctx.drawImage(sprite, p.x - s, p.y - s, s * 2, s * 2);
    }
    ctx.globalAlpha = 1;
    ps.length = alive;

    if (alive > 0) {
      window.requestAnimationFrame(step);
    } else {
      ps.length = 0;
      running = false;
      ctx.clearRect(0, 0, cv.width, cv.height);
    }
  }

  /* event delegation so re-rendered drawer contents keep working */
  document.addEventListener('click', function (e) {
    var b =
      e.target.closest &&
      e.target.closest('[data-forge-atc], [data-forge-sticky-atc], .forge-upsell .add');
    if (!b || b.disabled) {
      return;
    }
    var x = e.clientX;
    var y = e.clientY;
    if (!x && !y) {
      /* keyboard activation — burst from the control itself */
      var r = b.getBoundingClientRect();
      x = r.left + r.width / 2;
      y = r.top + r.height / 2;
    }
    burst(x || window.innerWidth / 2, y || window.innerHeight * 0.7);
  });
})();
